/* SmileNowDenture — interaction layer
   Loaded with `defer` from layout/theme.liquid. Vanilla JS, no jQuery dependency. */

(function () {
  'use strict';

  function initFaq(root) {
    var items = root.querySelectorAll('.snd-faq__item');
    items.forEach(function (item) {
      var q = item.querySelector('.snd-faq__q');
      if (!q) return;
      q.addEventListener('click', function () {
        var open = item.getAttribute('aria-expanded') === 'true';
        items.forEach(function (other) {
          other.setAttribute('aria-expanded', 'false');
          var btn = other.querySelector('.snd-faq__q');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
        if (!open) {
          item.setAttribute('aria-expanded', 'true');
          q.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  function initBeforeAfter(pair) {
    var media = pair.querySelector('.snd-ba__media');
    var after = pair.querySelector('.snd-ba__after');
    var handle = pair.querySelector('.snd-ba__handle');
    if (!media || !after || !handle) return;

    var setPos = function (pct) {
      var v = Math.max(2, Math.min(98, pct));
      // Reveal AFTER image to the right of the handle; clip its left side.
      var clip = 'inset(0 0 0 ' + v + '%)';
      after.style.clipPath = clip;
      after.style.webkitClipPath = clip;
      handle.style.left = v + '%';
    };

    var pointerMove = function (clientX) {
      var rect = media.getBoundingClientRect();
      var pct = ((clientX - rect.left) / rect.width) * 100;
      setPos(pct);
    };

    var dragging = false;
    media.addEventListener('mousedown', function (e) {
      dragging = true;
      pointerMove(e.clientX);
      e.preventDefault();
    });
    document.addEventListener('mousemove', function (e) {
      if (dragging) pointerMove(e.clientX);
    });
    document.addEventListener('mouseup', function () { dragging = false; });

    media.addEventListener('touchstart', function (e) {
      dragging = true;
      pointerMove(e.touches[0].clientX);
    }, { passive: true });
    media.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      pointerMove(e.touches[0].clientX);
    }, { passive: true });
    media.addEventListener('touchend', function () { dragging = false; });

    setPos(50);
  }

  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach(function (a) {
      var href = a.getAttribute('href');
      if (href === '#' || href.length < 2) return;
      a.addEventListener('click', function (e) {
        var target = document.getElementById(href.slice(1));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function initCustomersCarousel(root) {
    var track = root.querySelector('.snd-customers__track');
    var prev = root.querySelector('[data-snd-prev]');
    var next = root.querySelector('[data-snd-next]');
    var dotsWrap = root.querySelector('.snd-customers__dots');
    if (!track) return;

    var scrollByCard = function (dir) {
      var card = track.querySelector('.snd-customers__card');
      if (!card) return;
      var gap = parseInt(getComputedStyle(track).gap, 10) || 0;
      var step = card.offsetWidth + gap;
      // Wrap to start when we've reached (or are about to pass) the end
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (dir > 0 && atEnd) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: dir * step, behavior: 'smooth' });
      }
    };

    if (prev) prev.addEventListener('click', function () { scrollByCard(-1); });
    if (next) next.addEventListener('click', function () { scrollByCard(1); });

    if (dotsWrap) {
      var cards = track.querySelectorAll('.snd-customers__card');
      cards.forEach(function (card, i) {
        var btn = document.createElement('button');
        btn.className = 'snd-customers__dot';
        btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        if (i === 0) btn.setAttribute('aria-current', 'true');
        btn.addEventListener('click', function () {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        });
        dotsWrap.appendChild(btn);
      });

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var idx = Array.prototype.indexOf.call(cards, entry.target);
          if (idx < 0) return;
          dotsWrap.querySelectorAll('.snd-customers__dot').forEach(function (d, i) {
            if (i === idx) d.setAttribute('aria-current', 'true');
            else d.removeAttribute('aria-current');
          });
        });
      }, { root: track, threshold: 0.6 });
      cards.forEach(function (card) { io.observe(card); });
    }
  }

  function initVideoAutoplay() {
    var videos = document.querySelectorAll('.snd-reel video, .snd-process-video__media video');
    if (!videos.length || !('IntersectionObserver' in window)) {
      videos.forEach(function (v) { v.setAttribute('controls', ''); });
      return;
    }
    var activated = new WeakSet();

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        // Once a user has tapped to activate sound, don't pause-on-scroll handoff
        if (activated.has(v)) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          v.muted = true;
          var p = v.play();
          if (p && typeof p.catch === 'function') p.catch(function () { v.setAttribute('controls', ''); });
        } else {
          v.pause();
        }
      });
    }, { threshold: [0, 0.5, 1] });

    videos.forEach(function (v) {
      io.observe(v);

      // Tap-to-play with sound: restart from beginning, unmute, show controls
      var reel = v.closest('.snd-reel') || v.parentElement;
      if (!reel) return;
      reel.style.cursor = 'pointer';

      var activate = function (e) {
        if (activated.has(v)) return; // already activated, let native controls handle it
        e.preventDefault();
        // Pause any other activated video
        videos.forEach(function (other) {
          if (other !== v && activated.has(other)) {
            other.pause();
            other.muted = true;
            other.removeAttribute('controls');
            activated.delete(other);
          }
        });
        v.currentTime = 0;
        v.muted = false;
        v.setAttribute('controls', '');
        v.removeAttribute('loop');
        activated.add(v);
        var p = v.play();
        if (p && typeof p.catch === 'function') {
          p.catch(function () {
            // Autoplay-with-sound blocked → leave controls visible, user can press play
          });
        }
      };

      reel.addEventListener('click', activate);
      reel.addEventListener('touchend', activate);
    });
  }

  function initProcessPage() {
    var root = document.querySelector('[data-snd-process-content]');
    if (!root || root.getAttribute('data-snd-enhanced') === 'true') return;
    root.setAttribute('data-snd-enhanced', 'true');

    var children = Array.prototype.slice.call(root.children);
    if (!children.length) return;

    var firstStepIndex = -1;
    for (var i = 0; i < children.length; i += 1) {
      if (children[i].tagName && children[i].tagName.toLowerCase() === 'h4') {
        firstStepIndex = i;
        break;
      }
    }

    if (firstStepIndex > 0) {
      var hero = document.createElement('div');
      var heroCopy = document.createElement('div');
      var heroMedia = document.createElement('div');
      hero.className = 'snd-process-hero';
      heroCopy.className = 'snd-process-hero__copy';
      heroMedia.className = 'snd-process-hero__media';
      root.insertBefore(hero, root.firstChild);
      hero.appendChild(heroCopy);
      hero.appendChild(heroMedia);

      children.slice(0, firstStepIndex).forEach(function (node) {
        if (node.tagName && node.tagName.toLowerCase() === 'img') {
          heroMedia.appendChild(node);
        } else {
          heroCopy.appendChild(node);
        }
      });

      if (!heroMedia.children.length) {
        hero.className += ' snd-process-hero--no-media';
      }
    }

    var stepHeadings = Array.prototype.slice.call(root.children).filter(function (node) {
      return node.tagName && node.tagName.toLowerCase() === 'h4';
    });
    if (stepHeadings.length) {
      var stepGrid = document.createElement('div');
      stepGrid.className = 'snd-process-card-grid';
      root.insertBefore(stepGrid, stepHeadings[0]);

      stepHeadings.forEach(function (heading) {
        var card = document.createElement('article');
        var next = heading.nextElementSibling;
        card.className = 'snd-process-card';
        stepGrid.appendChild(card);
        card.appendChild(heading);
        if (next && next.tagName && next.tagName.toLowerCase() === 'p') {
          card.appendChild(next);
        }
      });
    }

    var directChildren = Array.prototype.slice.call(root.children);
    var activeSection = null;
    directChildren.forEach(function (node) {
      if (!node.tagName) return;
      if (node.classList.contains('snd-process-hero') || node.classList.contains('snd-process-card-grid')) return;
      if (node.tagName.toLowerCase() === 'h3') {
        activeSection = document.createElement('section');
        activeSection.className = 'snd-process-section';
        root.insertBefore(activeSection, node);
        activeSection.appendChild(node);
        return;
      }
      if (activeSection && !node.classList.contains('snd-process-section')) {
        activeSection.appendChild(node);
      }
    });
  }

  function initLegacyFaqDividers() {
    var sets = document.querySelectorAll('.snd-page--faq .dt-sc-toggle-frame-set');
    sets.forEach(function (set) {
      var heads = set.querySelectorAll('.dt-sc-toggle-accordion');
      heads.forEach(function (head) {
        var content = head.nextElementSibling;
        if (!content || !content.classList.contains('dt-sc-toggle-content')) return;
        // Strip nbsp + whitespace; if empty, treat the head as a section-divider, not an accordion.
        var text = (content.textContent || '').replace(/ /g, '').trim();
        if (text === '') {
          head.classList.add('snd-faq-divider');
          content.classList.add('snd-faq-divider__content');
        }
      });
    });
  }

  function init() {
    document.querySelectorAll('.snd-faq').forEach(initFaq);
    document.querySelectorAll('.snd-ba__pair').forEach(initBeforeAfter);
    document.querySelectorAll('.snd-customers').forEach(initCustomersCarousel);
    initLegacyFaqDividers();
    initProcessPage();
    initVideoAutoplay();
    initSmoothAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
